import React, {useState, useEffect} from 'react';
import {Collapse, Grid, Stack, Typography} from '@mui/material';
import {ISelectItem} from '../../../../../../shared/types';
import {Field} from '../../../../types/Field';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import {t} from 'i18next';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrNumericInput from '../../../../../common/arr-numeric-input/ArrNumericInput';
import '../FieldTypes.scss';
import {IWorkProductItemsDetail} from '../../../../../../api/types/sessionSearch.type';
import {
  useFieldData,
  useOperatorData,
  useAutocomplete,
} from '../../../../../../hooks/session-search/useSessionSearch';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Trans} from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrAutocomplete from '../../../../../common/arr-autocomplete/ArrAutocomplete';
import {formatAutocompleteResults} from '../../autocompleteUtils';

export interface AnalyticsFieldTypeProps {
  onInputValid?: (valid: boolean) => void;
  onCriteriaChaged?: (field: Field, operator: string, value: string) => void;
}

const AnalyticsFieldType: React.FC<AnalyticsFieldTypeProps> = ({
  onInputValid,
  onCriteriaChaged,
}) => {
  const [selectItems, setSelectItems] = useState<ISelectItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ISelectItem | null>(null);
  const [selectedField, setSelectedField] =
    useState<IWorkProductItemsDetail | null>(null);
  const [selectOperators, setSelectOperators] = useState<ISelectItem[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<ISelectItem | null>(
    null
  );
  const [startNumericValue, setStartNumericValue] = useState<number | null>();
  const [endNumericValue, setEndNumericValue] = useState<number | null>();
  const [textValue, setTextValue] = useState<string>('');
  const [showAnalyticsFieldsToast, setShowAnalyticsFieldsToast] =
    useState(true);
  const [isHelpExpanded, setIsHelpExpanded] = useState<boolean>(false);
  const [
    showAnalyticsFieldsOperatorToast,
    setShowAnalyticsFieldsOperatorToast,
  ] = useState(true);
  const {
    data: fieldsList,
    isError: analyticsFieldsError,
    isPending: isLoading,
  } = useFieldData('ANALYTICSLIST');
  const {
    data: operatorList,
    isError: analyticsFieldsOperatorError,
    isPending: isOperatorsLoading,
  } = useOperatorData(selectedField?.type ?? '');
  const [autocompleteValue, setAutocompleteValue] = useState<string>('');
  const [autocompleteEnabled, setAutocompleteEnabled] =
    useState<boolean>(false);
  const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
  const {data: autocompleteData, refetch} = useAutocomplete(autocompleteValue);
  const [numberRangeError, setNumberRangeError] = useState<boolean>(false);
  const [numberErrorText, setNumberErrorText] = useState<string>('');

  useEffect(() => {
    if (!analyticsFieldsError && !isLoading && fieldsList) {
      const mappedItems = fieldsList?.workProductItemsDetails.map((value) => ({
        value: value.id,
        label: value.text,
      }));
      setSelectItems(mappedItems);
      setSelectedItem(null);
    }
  }, [fieldsList, analyticsFieldsError, isLoading]);

  useEffect(() => {
    if (operatorList) {
      const selectOperators = operatorList.map((value) => {
        return {value: value.operatorCode, label: value.operatorLabel};
      });
      setSelectOperators(selectOperators);
      setSelectedOperator(selectOperators[0]);
    }
  }, [operatorList, analyticsFieldsOperatorError, isOperatorsLoading]);

  const IsInputValid = (selectedOperatorValue: string): boolean => {
    const excludedOperators = ['ISEMPTY', 'ISPOPULATED', 'ISTRUE', 'ISFALSE'];

    // Case 1: excluded operators are always valid (no value required)
    if (excludedOperators.includes(selectedOperatorValue)) {
      return true;
    }

    // Case 2: NVARCHAR field with text
    if (selectedField?.type === 'NVARCHAR') {
      return !!textValue; // true if non-empty string
    }

    // Case 3: INT field with numeric values
    if (selectedField?.type === 'INT') {
      setNumberRangeError(false);
      setNumberErrorText('');

      const hasStart = startNumericValue != null;

      if (selectedOperatorValue === 'ISBETWEEN') {
        const hasEnd = endNumericValue != null;
        const hasRange = hasStart && hasEnd;

        let startComesBeforeEnd = false;
        if (hasRange) {
          startComesBeforeEnd = endNumericValue >= startNumericValue;
        }

        // A range is provided but the start and end are not valid
        if (hasRange && !startComesBeforeEnd) {
          setNumberRangeError(true);
          setNumberErrorText(
            t(
              'sessionSearch.queryList.analytics.analyticsDrawer.numberRangeError'
            )
          );
        }

        return hasRange && startComesBeforeEnd;
      } else {
        return hasStart;
      }
    }

    // Default: not valid
    return false;
  };

  useEffect(() => {
    if (textValue && textValue.length >= 3) {
      setAutocompleteValue(`${selectedField?.text}:${textValue}`);
    }
  }, [textValue]);

  useEffect(() => {
    refetch();
  }, [autocompleteValue]);

  const getFieldValue = (): string => {
    let fieldValue: string = '';
    const excludedOperators = ['ISEMPTY', 'ISPOPULATED', 'ISTRUE', 'ISFALSE'];

    const operatorValue =
      selectedOperator?.value?.toString().toUpperCase() ?? '';

    // Case 1: excluded operators → always return empty string
    if (excludedOperators.includes(operatorValue)) {
      return '';
    }

    // Case 2: NVARCHAR field → use text value safely
    if (selectedField?.type === 'NVARCHAR') {
      fieldValue = textValue ?? '';
    }

    // Case 3: INT field → handle between vs single value
    if (selectedField?.type === 'INT') {
      if (operatorValue === 'ISBETWEEN') {
        const start = startNumericValue ?? '';
        const end = endNumericValue ?? '';
        fieldValue = `${start} TO ${end}`;
      } else {
        fieldValue =
          startNumericValue != null ? startNumericValue.toString() : '';
      }
    }

    return fieldValue;
  };

  useEffect(() => {
    // Set field when an item is selected
    let field: IWorkProductItemsDetail | null = null;
    if (selectedItem) {
      field =
        fieldsList?.workProductItemsDetails.find(
          (value) => value.id === selectedItem.value
        ) ?? null;
      setSelectedField(field);
    }
    const operator = selectedOperator?.value
      ? selectedOperator?.value.toString()
      : '';
    if (field) {
      onCriteriaChaged && onCriteriaChaged(field, operator, getFieldValue());
    }

    onInputValid && onInputValid(IsInputValid(operator));
  }, [
    selectedItem,
    selectedOperator,
    textValue,
    startNumericValue,
    endNumericValue,
  ]);

  useEffect(() => {
    const suggestions = formatAutocompleteResults(autocompleteData ?? []);
    setAutocompleteItems(suggestions);
  }, [autocompleteData]);

  const IsValueFieldVisible = () => {
    const excludedOperators = ['ISEMPTY', 'ISPOPULATED', 'ISTRUE', 'ISFALSE'];
    const selectedOperatorValue = selectedOperator?.value
      ? selectedOperator?.value.toString()
      : '';
    return !excludedOperators.includes(selectedOperatorValue);
  };

  const selectedFieldChanged = (selectedItem: ISelectItem | null) => {
    setSelectedItem(selectedItem);
  };

  const seperator = (selectedOperatorValue: string) => {
    let component = <></>;
    if (selectedOperatorValue === 'ISBETWEEN') {
      component = <RemoveIcon className="range-separator-icon" />;
    }
    return component;
  };

  const fromLabel = (selectedOperatorValue: string) => {
    if (selectedOperatorValue === 'ISBETWEEN') {
      return t('sessionSearch.queryList.analytics.analyticsDrawer.startNumber');
    } else {
      return t('sessionSearch.queryList.analytics.analyticsDrawer.enterValue');
    }
  };

  const renderNumericComponent = () => {
    const selectedOperatorValue = selectedOperator?.value
      ? selectedOperator?.value.toString()
      : '';

    const component = (
      <Grid container spacing={2}>
        <Grid size="grow" className="range-start">
          <ArrNumericInput
            value={startNumericValue ?? undefined}
            label={fromLabel(selectedOperatorValue)}
            onChange={setStartNumericValue}
            sizeVariant="large"
            dataTestId="number-field-start"
            showPercentage={false}
          />
        </Grid>

        <Grid className="range-separator">
          {seperator(selectedOperatorValue)}
        </Grid>
        <Grid
          size="grow"
          className="range-end"
          style={{
            visibility:
              selectedOperatorValue !== 'ISBETWEEN' ? 'hidden' : 'visible',
          }}
        >
          <ArrNumericInput
            value={endNumericValue ?? undefined}
            label={t(
              'sessionSearch.queryList.analytics.analyticsDrawer.endNumber'
            )}
            onChange={setEndNumericValue}
            sizeVariant="large"
            dataTestId="number-field-end"
            showPercentage={false}
            disabled={selectedOperatorValue !== 'ISBETWEEN'}
            isError={numberRangeError}
            supportText={numberErrorText}
          />
        </Grid>
      </Grid>
    );
    return component;
  };

  const renderValueComponent = () => {
    let component = <></>;
    if (IsValueFieldVisible()) {
      if (selectedField?.type == 'NVARCHAR') {
        component = (
          <Grid container spacing={1}>
            <Grid size={12}>
              <ArrAutocomplete
                value={textValue}
                options={autocompleteItems}
                onChange={setTextValue}
                enabled={autocompleteEnabled}
                data-testid="analytics-field-type-autocomplete"
              >
                <ArrTextArea
                  placeholder={t(
                    'sessionSearch.queryList.analytics.analyticsDrawer.enterValue'
                  )}
                  rows={6}
                  value={textValue}
                  onChange={setTextValue}
                  resizable={true}
                  onFocus={() => setAutocompleteEnabled(true)}
                  onBlur={() => setAutocompleteEnabled(false)}
                  dataTestId={`text-area-analytics-field`}
                />
              </ArrAutocomplete>
            </Grid>
            <Grid className="field-type-help">
              <Stack spacing={2}>
                <ArrButton
                  variant="text"
                  startIcon={<InfoOutlinedIcon />}
                  endIcon={
                    isHelpExpanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
                  }
                  onClick={() => setIsHelpExpanded((value) => !value)}
                  size="small"
                  className="metadata-search-help"
                  dataTestId="metadata-search-help"
                >
                  {t(
                    'sessionSearch.queryList.analytics.analyticsDrawer.searchHelpLabel'
                  )}
                </ArrButton>
                <Collapse in={isHelpExpanded}>
                  <Typography
                    className="typo-text body5_size"
                    data-testid={`analytics-field-type-typography`}
                  >
                    <Trans i18nKey="sessionSearch.queryList.analytics.analyticsDrawer.searchHelpContent"></Trans>
                  </Typography>
                </Collapse>
              </Stack>
            </Grid>
          </Grid>
        );
      } else if (selectedField?.type == 'INT') {
        component = renderNumericComponent();
      }
    } else {
      return null;
    }
    return component;
  };

  return (
    <>
      <Stack spacing={2} className="field-type-content">
        <Grid container spacing={2} className="field-type-selection">
          <Grid size="grow" className="field-selection">
            <ArrSingleSelectDropdown
              dropdownLabel={t(
                'sessionSearch.queryList.analytics.analyticsDrawer.selectValue'
              )}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              dropdownItems={selectItems}
              selectedValues={selectedItem}
              onSelectionChange={selectedFieldChanged}
              sizeVariant="large"
              dataTestId="asd-analytics-field-type-dropdown"
              searchable
              showNoResultsText
            />
          </Grid>
          <Grid className="field-separator"></Grid>
          <Grid size="grow" className="field-operator">
            {selectedItem && (
              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'sessionSearch.queryList.analytics.analyticsDrawer.selectOperator'
                )}
                dropdownItemKey="value"
                dropdownItemDisplay="label"
                dropdownItems={selectOperators}
                selectedValues={selectedOperator}
                onSelectionChange={setSelectedOperator}
                sizeVariant="large"
                dataTestId="asd-analytics-filed-operator-dropdown"
              />
            )}
          </Grid>
        </Grid>
        <Grid className="field-type-value">{renderValueComponent()}</Grid>
      </Stack>
      {analyticsFieldsError && showAnalyticsFieldsToast && (
        <ArrToastNotification
          message={t(
            'sessionSearch.queryList.analytics.analyticsDrawer.analyticsFieldsError'
          )}
          status={[ToastType.Error]}
          onClose={() => setShowAnalyticsFieldsToast(false)}
        />
      )}
      {analyticsFieldsOperatorError && showAnalyticsFieldsOperatorToast && (
        <ArrToastNotification
          message={t(
            'sessionSearch.queryList.analytics.analyticsDrawer.analyticsFieldsOperatorError'
          )}
          status={[ToastType.Error]}
          onClose={() => setShowAnalyticsFieldsOperatorToast(false)}
        />
      )}
    </>
  );
};

export default AnalyticsFieldType;
